/* eslint sort-keys: "error", sort-exports/sort-exports: "error" */

import { makeVar } from "@apollo/client";
import type { RefObject } from "react";

import type { AssetUri } from "~/components/blocks/vod/playback/hooks/use-generate-asset-uri";
import { isTouchDisplay } from "~/const/device";
import type { G_VideoContentType } from "~/lib/graphql";

/**
 * @see https://confl.arms.dmm.com/pages/viewpage.action?pageId=558530667
 */
export type Heatmap = {
  timestamps: number[];
  values: number[];
  length: number;
};

// NOTE: 0.06でplaybackRateを指定するとDOM Exceptionが出る為0.0625と指定することでそれを回避している
export const PLAYBACK_RATE = ["0.0625", "0.25", "0.5", "0.75", "1.0", "1.25", "1.5", "1.75", "2.0"] as const;
export type PlaybackRate = (typeof PLAYBACK_RATE)[number];

export const SECONDS = [10, 30, 60] as const;
export type Seconds = (typeof SECONDS)[number];

export const initialState = {
  /** shaka-player用音声設定 */
  audioLang: "",
  /** ブロックしたユーザーリスト */
  blockUsers: [],
  /** シークサムネイルが表示可能かどうかを判定 */
  canSeekThumbnailVisible: true,
  /** コントローラーの非表示を制御するタイマーID */
  controllerTimerId: 0,
  /** 現在の再生時間 */
  currentTime: 0,
  /** 音量 */
  currentVolume: 1,
  /** 総再生時間 */
  duration: 0,
  /** ヒートマップのデータ */
  heatmap: null,
  /** 自動再生がブロックされているかを判定 */
  isAutoPlayBlocked: false,
  /** コメントの表示判定 */
  isCommentVisible: true,
  /** コメントを入力中かどうかを判定 */
  isCommenting: false,
  /** 連続再生をするかどうかを判定 */
  isContinuousPlaybackEnabled: true,
  /** コントローラーの表示判定 */
  isControllerVisible: !isTouchDisplay,
  /** アイコンのopacityを用いたアニメーションが実行中かどうか */
  isDuringOpacityAnimation: false,
  /** 映像再生が終了しているかどうか*/
  isEnded: false,
  /** エピソード一覧の表示判定 */
  isEpisodeListVisible: false,
  /** 全画面表示中かどうかを判定 */
  isFullscreen: false,
  /** ヒートマップの表示判定 */
  isHeatmapVisible: true,
  /** データを読み込み中かを判定 */
  isLoadingData: true,
  /** メタデータを読み込み中かを判定 */
  isLoadingMetadata: true,
  /** ミュート/アンミュートを判定 */
  isMuted: false,
  /** 「次のエピソード」ブロックのxボタンを押下したか */
  isNextEpisodeCloseIconPressed: false,
  /** 「次のエピソード」の表示判定 */
  isNextEpisodeDialogVisible: false,
  /** パート選択ポップアップの表示判定 */
  isPartsPopupVisible: false,
  /** 再生/停止を判定 */
  isPaused: true,
  /** 再生速度設定ポップアップの表示判定 */
  isPlaybackPopupVisible: false,
  /** 再生ボタンを押したかどうか */
  isPushedButton: false,
  /** 予告編動画かどうかを判定*/
  isPv: false,
  /** レコメンド一覧の表示判定 */
  isRecommendListVisible: false,
  /** サンプル動画かどうかを判定 */
  isSample: false,
  /** シークサムネイルが表示されているかどうか */
  isSeekThumbnailVisible: false,
  /** 設定ポップアップが開いているかどうかを判定 */
  isSettingsVisible: false,
  /** 字幕ONLYなコンテンツ化どうかを判定 */
  isTextOnly: false,
  /** 再生ボタン要素*/
  playButtonRef: {
    current: null,
  },
  /** 再生速度 */
  playbackRate: PLAYBACK_RATE[4],
  /** templateコンポーネントをラップする要素 */
  playbackTemplateWrapper: {
    current: null,
  },
  /** shaka.Player */
  playerRef: {
    current: null,
  },
  /** プレイヤー画面回遊時の1つ前のビデオの種類 */
  previousContentType: null,
  /** 表示名 (AUTO, SD, HD(1080p) 等) */
  qualityDisplayName: "AUTO",
  /** 品質識別子 (auto, sd, hd, 4k 等) */
  qualityName: "auto",
  /** 解像度とフレームレート情報 */
  resolutionAndFrameRate: "",
  /** 早戻しの秒数 */
  rewindNSeconds: SECONDS[0],
  /** 早送りの秒数 */
  skipNSeconds: SECONDS[0],
  /** ビデオ要素のsrc属性に渡す値 */
  src: [],
  /** 画質等の切り替えをした際の引き継ぎ用の再生時間 */
  takeoverCurrentTime: 0,
  /** shaka-player用字幕設定 */
  textLang: "",

  /** ビデオ要素 */
  videoRef: {
    current: null,
  },
};

// NOTE: initialStateの前に変数がsortされないように
// eslint-disable-next-line sort-exports/sort-exports
export const audioLangVar = makeVar(initialState.audioLang);
export const blockUsersVar = makeVar<string[]>(initialState.blockUsers);
export const canSeekThumbnailVisibleVar = makeVar(initialState.canSeekThumbnailVisible);
export const controllerTimerIdVar = makeVar(initialState.controllerTimerId);
export const currentTimeVar = makeVar(initialState.currentTime);
export const currentVolumeVar = makeVar(initialState.currentVolume);
export const durationVar = makeVar(initialState.duration);
export const heatmapVar = makeVar<Heatmap | null>(initialState.heatmap);
export const isAutoPlayBlockedVar = makeVar(initialState.isAutoPlayBlocked);
export const isCommentVisibleVar = makeVar(initialState.isCommentVisible);
export const isCommentingVar = makeVar(initialState.isCommenting);
export const isContinuousPlaybackEnabledVar = makeVar(initialState.isContinuousPlaybackEnabled);
export const isControllerVisibleVar = makeVar(initialState.isControllerVisible);
export const isDuringOpacityAnimationVar = makeVar(initialState.isDuringOpacityAnimation);
export const isEndedVar = makeVar(initialState.isEnded);
export const isEpisodeListVisibleVar = makeVar(initialState.isEpisodeListVisible);
export const isFullscreenVar = makeVar(initialState.isFullscreen);
export const isHeatmapVisibleVar = makeVar(initialState.isHeatmapVisible);
export const isLoadingDataVar = makeVar(initialState.isLoadingData);
export const isLoadingMetadataVar = makeVar(initialState.isLoadingMetadata);
export const isMutedVar = makeVar(initialState.isMuted);
export const isNextEpisodeCloseIconPressedVar = makeVar(initialState.isNextEpisodeCloseIconPressed);
export const isNextEpisodeDialogVisibleVar = makeVar(initialState.isNextEpisodeDialogVisible);
export const isPartsPopupVisibleVar = makeVar(initialState.isPartsPopupVisible);
export const isPausedVar = makeVar(initialState.isPaused);
export const isPlaybackPopupVisibleVar = makeVar(initialState.isPlaybackPopupVisible);
export const isPushedButtonVar = makeVar(initialState.isPushedButton);
export const isPvVar = makeVar(initialState.isPv);
export const isRecommendListVisibleVar = makeVar(initialState.isRecommendListVisible);
export const isSampleVar = makeVar(initialState.isSample);
export const isSeekThumbnailVisibleVar = makeVar(initialState.isSeekThumbnailVisible);
export const isSettingsVisibleVar = makeVar(initialState.isSettingsVisible);
export const isTextOnlyVar = makeVar(initialState.isTextOnly);
export const playButtonRefVar = makeVar<RefObject<HTMLButtonElement>>(initialState.playButtonRef);
export const playbackRateVar = makeVar<(typeof PLAYBACK_RATE)[number]>(initialState.playbackRate);
export const playbackTemplateWrapperVar = makeVar<RefObject<HTMLDivElement>>(initialState.playbackTemplateWrapper);
export const playerRefVar = makeVar<RefObject<shaka.Player>>(initialState.playerRef);
export const previousContentTypeVar = makeVar<G_VideoContentType | null>(initialState.previousContentType);
export const qualityDisplayNameVar = makeVar(initialState.qualityDisplayName);
export const qualityNameVar = makeVar(initialState.qualityName);
export const resolutionAndFrameRateVar = makeVar(initialState.resolutionAndFrameRate);
export const rewindNSecondsVar = makeVar<Seconds>(initialState.rewindNSeconds);
export const skipNSecondsVar = makeVar<Seconds>(initialState.skipNSeconds);
export const srcVar = makeVar<AssetUri>(initialState.src);
export const takeoverCurrentTimeVar = makeVar(initialState.takeoverCurrentTime);
export const textLangVar = makeVar(initialState.textLang);
export const videoRefVar = makeVar<RefObject<HTMLVideoElement>>(initialState.videoRef);

/**
 * 以下以外全てのStateをリセットする
 * qualityNameVar:サイトの回遊中は保持させるため
 * qualityDisplayNameVar:サイトの回遊中は保持させるため
 * isMuted:離脱中に音が出ることを回避するため
 * previousContentTypeVar:Player離脱時に個別でリセットさせるため
 */
// eslint-disable-next-line sort-exports/sort-exports
export const useResetPlaybackState = () => {
  const resetPlaybackState = () => {
    audioLangVar(initialState.audioLang);
    controllerTimerIdVar(initialState.controllerTimerId);
    canSeekThumbnailVisibleVar(initialState.canSeekThumbnailVisible);
    currentTimeVar(initialState.currentTime);
    currentVolumeVar(initialState.currentVolume);
    durationVar(initialState.duration);
    heatmapVar(initialState.heatmap);
    isAutoPlayBlockedVar(initialState.isAutoPlayBlocked);
    isCommentVisibleVar(initialState.isCommentVisible);
    isCommentingVar(initialState.isCommenting);
    isContinuousPlaybackEnabledVar(initialState.isContinuousPlaybackEnabled);
    isControllerVisibleVar(initialState.isControllerVisible);
    isDuringOpacityAnimationVar(initialState.isDuringOpacityAnimation);
    isEndedVar(initialState.isEnded);
    isEpisodeListVisibleVar(initialState.isEpisodeListVisible);
    isFullscreenVar(initialState.isFullscreen);
    isHeatmapVisibleVar(initialState.isHeatmapVisible);
    isLoadingDataVar(initialState.isLoadingData);
    isLoadingMetadataVar(initialState.isLoadingMetadata);
    isNextEpisodeCloseIconPressedVar(initialState.isNextEpisodeCloseIconPressed);
    isNextEpisodeDialogVisibleVar(initialState.isNextEpisodeDialogVisible);
    isPartsPopupVisibleVar(initialState.isPartsPopupVisible);
    isPausedVar(initialState.isPaused);
    isPlaybackPopupVisibleVar(initialState.isPlaybackPopupVisible);
    isPushedButtonVar(initialState.isPushedButton);
    isPvVar(initialState.isPv);
    isRecommendListVisibleVar(initialState.isRecommendListVisible);
    isSampleVar(initialState.isSample);
    isSeekThumbnailVisibleVar(initialState.isSeekThumbnailVisible);
    isSettingsVisibleVar(initialState.isSettingsVisible);
    isTextOnlyVar(initialState.isTextOnly);
    playButtonRefVar(initialState.playButtonRef);
    playbackRateVar(initialState.playbackRate);
    playerRefVar(initialState.playerRef);
    playbackTemplateWrapperVar(initialState.playbackTemplateWrapper);
    resolutionAndFrameRateVar(initialState.resolutionAndFrameRate);
    rewindNSecondsVar(initialState.rewindNSeconds);
    skipNSecondsVar(initialState.skipNSeconds);
    srcVar(initialState.src);
    takeoverCurrentTimeVar(initialState.takeoverCurrentTime);
    textLangVar(initialState.textLang);
    videoRefVar(initialState.videoRef);
  };

  return {
    resetPlaybackState,
  };
};
